import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { subscribeNewsletter } from '../services/api';
import { Award, Leaf, Truck, Hammer } from 'lucide-react';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    // Basic email validation
    const isValid = /[^@\s]+@[^@\s]+\.[^@\s]+/.test(trimmed);
    if (!isValid) {
      toast.error('Please enter a valid email address.');
      return;
    }
    try {
      setSubmitting(true);
      const data = await subscribeNewsletter(trimmed);
      toast.success(data?.message || 'Subscribed successfully!');
      setEmail('');
    } catch (err) {
      toast.error(err.message || 'Failed to subscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <footer className="bg-white dark:bg-gray-900 transition-colors">
      {/* Benefits Section */}
      <div className="py-12 lg:py-16 px-4 sm:px-6 bg-gray-50 dark:bg-gray-800 transition-colors">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            
            {/* Award-Winning Design */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                <Award className="w-7 h-7 text-orange-500" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 dark:text-white text-lg mb-1">Award-Winning Design</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">Recognized globally for innovative furniture designs</p>
              </div>
            </div>

            {/* Sustainable Materials */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                <Leaf className="w-7 h-7 text-green-500" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 dark:text-white text-lg mb-1">Sustainable Materials</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">Eco-friendly production with responsibly sourced materials</p>
              </div>
            </div>

            {/* Fast Delivery */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                <Truck className="w-7 h-7 text-blue-500" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 dark:text-white text-lg mb-1">Fast Delivery</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">Quick and reliable shipping to your doorstep</p>
              </div>
            </div>

            {/* Expert Craftsmanship */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                <Hammer className="w-7 h-7 text-purple-500" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 dark:text-white text-lg mb-1">Expert Craftsmanship</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">Handcrafted with attention to every detail</p>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Newsletter Subscribe */}
      <div className="px-4 sm:px-6 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 transition-colors">
        <div className="max-w-7xl mx-auto py-8">
          <div className="rounded-2xl bg-orange-50 dark:bg-gray-800/60 p-6 sm:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
              <div className="lg:col-span-2">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Subscribe to our Newsletter</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Get the latest deals, new arrivals, and design tips delivered to your inbox.</p>
              </div>
              <form onSubmit={subscribe} className="w-full">
                <div className="flex rounded-lg shadow-sm overflow-hidden border border-orange-200 bg-white dark:bg-gray-900 dark:border-gray-700">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 text-sm outline-none bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400"
                    disabled={submitting}
                    required
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-3 text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60"
                  >
                    {submitting ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  By subscribing, you agree to receive marketing emails. You can unsubscribe at any time.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-12 lg:py-16 px-4 sm:px-6 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 transition-colors">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
            
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="/logo.png" 
                  alt="Brand Decor Logo" 
                  className="w-12 h-12 object-contain"
                />
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white">Brand Decor</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Furniture</p>
                </div>
              </div>
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 dark:text-white mb-3">About Brand Decor Furniture</h3>
                <h4 className="text-lg font-medium text-gray-700 dark:text-gray-200 mb-3">Creating Beautiful Spaces Since 2015</h4>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                  We believe that every home deserves beautiful, functional furniture that reflects your unique style. 
                  Our carefully curated collection combines modern design with timeless craftsmanship, 
                  creating pieces that transform houses into homes.
                </p>
              </div>
            </div>

            {/* Visit Link */}
            <div>
              <h3 className="font-bold text-gray-800 dark:text-white text-lg mb-6">Quick Links</h3>
              <ul className="space-y-3">
                <li><a href="/privacy-policy" className="text-gray-600 dark:text-gray-300 hover:text-orange-500 transition-colors">Privacy Policy</a></li>
                <li><a href="/terms-conditions" className="text-gray-600 dark:text-gray-300 hover:text-orange-500 transition-colors">Terms & Conditions</a></li>
                <li><a href="/delivery-info" className="text-gray-600 dark:text-gray-300 hover:text-orange-500 transition-colors">Delivery Info</a></li>
                <li><a href="/returns" className="text-gray-600 dark:text-gray-300 hover:text-orange-500 transition-colors">Returns</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-bold text-gray-800 dark:text-white text-lg mb-6">Company</h3>
              <ul className="space-y-3">
                <li><a href="/" className="text-gray-600 dark:text-gray-300 hover:text-orange-500 transition-colors">Home</a></li>
                <li><a href="/shop" className="text-gray-600 dark:text-gray-300 hover:text-orange-500 transition-colors">Shop</a></li>
                <li><a href="/about" className="text-gray-600 dark:text-gray-300 hover:text-orange-500 transition-colors">About</a></li>
                <li><a href="/contact" className="text-gray-600 dark:text-gray-300 hover:text-orange-500 transition-colors">Contact</a></li>
                <li><a href="/company" className="text-gray-600 dark:text-gray-300 hover:text-orange-500 transition-colors">Company</a></li>
              </ul>
            </div>

            {/* Contact & Address */}
            <div>
              <h3 className="font-bold text-gray-800 dark:text-white text-lg mb-6">Contact</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-600 dark:text-gray-300">(+254) 0714139461</p>
                  <p className="text-gray-600 dark:text-gray-300">sales@branddecorinteriors.co.ke</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-2">Address</h4>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Brand Decor Interiors<br />
                    Nairobi, Kenya<br />
                    East Africa
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="py-4 sm:py-6 px-4 sm:px-6 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 transition-colors">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 md:mb-0">
            © 2025 Brand Decor Furniture - Creating Beautiful Spaces. All rights reserved.
          </p>
          
          {/* Social Media Icons */}
          <div className="flex items-center space-x-4">
            <button className="w-8 h-8 bg-gray-100 dark:bg-gray-700 hover:bg-orange-500 rounded-full flex items-center justify-center group transition-all duration-300">
              <svg className="w-4 h-4 text-gray-600 dark:text-gray-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.083.346-.09.375-.293 1.199-.334 1.363-.053.225-.174.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001.017.001z"/>
              </svg>
            </button>
            
            <button className="w-8 h-8 bg-gray-100 dark:bg-gray-700 hover:bg-orange-500 rounded-full flex items-center justify-center group transition-all duration-300">
              <svg className="w-4 h-4 text-gray-600 dark:text-gray-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </button>
            
            <button className="w-8 h-8 bg-gray-100 dark:bg-gray-700 hover:bg-orange-500 rounded-full flex items-center justify-center group transition-all duration-300">
              <svg className="w-4 h-4 text-gray-600 dark:text-gray-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
              </svg>
            </button>
            
            <button className="w-8 h-8 bg-gray-100 dark:bg-gray-700 hover:bg-orange-500 rounded-full flex items-center justify-center group transition-all duration-300">
              <svg className="w-4 h-4 text-gray-600 dark:text-gray-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
